const DEBUG_LOG = false;
const DebugLogCss = "color: deeppink";

export type DebugPromiseStack = {
  stack: Error["stack"];
};

/** Augmented Promise that can generate extra debug information.
 * Use it like an ordinary Promise.
 * If it's rejected with no reason, the call stack will be recorded. */
export class DebugPromise<T> extends Promise<T> {
  static promiseId = 0;
  static unreasonableRejectStacks?: { [key: number]: DebugPromiseStack } = {};

  readonly promiseId: number;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(executor: (resolve: (value: T | PromiseLike<T>) => void, reject: (reason?: any) => void) => void) {
    const promiseId = DebugPromise.promiseId++;
    if (DEBUG_LOG) {
      console.log("%cDebugPromise.constructor", DebugLogCss, "id:", promiseId);
    }

    // Intercept the call from Promise to executor function supplied to the constructor,
    // replacing the reject function supplied by Promise to that executor with a function that
    // calls that reject function after checking if the rejection was without reason,
    // and logging some info if it was.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const debugExecutor = (resolve: (value: T | PromiseLike<T>) => void, reject: (reason?: any) => void) => {
      if (DEBUG_LOG) {
        console.log("%cDebugPromise.constructor::debugExecutor", DebugLogCss, "id:", promiseId);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const debugReject = (reason?: any): void => {
        // prettier-ignore
        DEBUG_LOG && console.log("%cDebugPromise.constructor::debugExecutor::debugReject", DebugLogCss,
          "id:", promiseId,
          "reason:", reason
        );

        if (reason == undefined && DebugPromise.unreasonableRejectStacks != undefined) {
          DebugPromise.unreasonableRejectStacks[promiseId] = {
            stack: new Error().stack,
          };
        }

        reject(reason);
      };
      executor(resolve, debugReject);
    };

    super(debugExecutor);

    DEBUG_LOG && console.log("%cDebugPromise.constructor this defined", DebugLogCss, "id:", promiseId);

    // Note: It's not clear this will always be set before debugReject is called.
    // Because 'this' is not accessible until super, and super runs debugExecutor immediately...
    // 'this' can't be used anywhere above the call to super.
    this.promiseId = promiseId;
  }
}

/** Replaces Promise with DebugPromise, which provides additional debugging of rejected promises. */
export function enableDebugPromise() {
  // eslint-disable-next-line no-global-assign
  Promise = DebugPromise;
  DEBUG_LOG && console.log("%cReplaced Promise with DebugPromise", DebugLogCss);
}
